import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type ReactNode,
  type RefCallback,
} from "react";
import { useTranslation } from "react-i18next";
import styles from "@/components/dateTime/genericTimePicker.module.css";
import type { GenericTimeValue } from "@/components/dateTime/genericTimeUtils";

export type { GenericTimeValue } from "@/components/dateTime/genericTimeUtils";

interface Props {
  value: GenericTimeValue;
  onChange: (value: GenericTimeValue) => void;
  onValidityChange?: (isValid: boolean) => void;
  disabled?: boolean;
  className?: string;
  presentation?: "side-panel" | "standalone";
  validationTargetRef?: RefCallback<HTMLElement>;
  validationMessage?: ReactNode;
}

type TimePart = "hour" | "minute";

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);
const MINUTES = Array.from({ length: 12 }, (_, index) => index * 5);

const formatPart = (value: number): string => String(value).padStart(2, "0");

const parsePart = (text: string, part: TimePart): number | null => {
  if (!/^\d{1,2}$/.test(text)) {
    return null;
  }
  const parsed = Number(text);
  const maximum = part === "hour" ? 23 : 59;
  return parsed <= maximum ? parsed : null;
};

export default function GenericTimePicker({
  value,
  onChange,
  onValidityChange,
  disabled = false,
  className,
  presentation = "side-panel",
  validationTargetRef,
  validationMessage,
}: Props) {
  const { t } = useTranslation();
  const hourOptionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const minuteOptionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [hourText, setHourText] = useState(formatPart(value.hour));
  const [minuteText, setMinuteText] = useState(formatPart(value.minute));

  useEffect(() => {
    setHourText(formatPart(value.hour));
    setMinuteText(formatPart(value.minute));
    onValidityChange?.(true);
  }, [value.hour, value.minute, onValidityChange]);

  useEffect(() => {
    hourOptionRefs.current[value.hour]?.scrollIntoView?.({ block: "center" });
    const minuteIndex = MINUTES.indexOf(value.minute);
    if (minuteIndex >= 0) {
      minuteOptionRefs.current[minuteIndex]?.scrollIntoView?.({
        block: "center",
      });
    }
  }, [value.hour, value.minute]);

  const selectPart = (part: TimePart, nextValue: number) => {
    onValidityChange?.(true);
    onChange({
      hour: part === "hour" ? nextValue : value.hour,
      minute: part === "minute" ? nextValue : value.minute,
    });
  };

  const updateFromText = (nextHourText: string, nextMinuteText: string) => {
    const hour = parsePart(nextHourText, "hour");
    const minute = parsePart(nextMinuteText, "minute");
    const isValid =
      nextHourText.length === 2 &&
      nextMinuteText.length === 2 &&
      hour !== null &&
      minute !== null;

    onValidityChange?.(isValid);
    if (isValid) {
      onChange({ hour, minute });
    }
  };

  const handleInputChange = (
    event: ChangeEvent<HTMLInputElement>,
    part: TimePart,
  ) => {
    const nextText = event.target.value.replace(/\D/g, "").slice(0, 2);
    const nextHourText = part === "hour" ? nextText : hourText;
    const nextMinuteText = part === "minute" ? nextText : minuteText;

    if (part === "hour") {
      setHourText(nextText);
    } else {
      setMinuteText(nextText);
    }
    updateFromText(nextHourText, nextMinuteText);
  };

  const handleInputBlur = (part: TimePart) => {
    const text = part === "hour" ? hourText : minuteText;
    const parsed = parsePart(text, part);

    if (parsed === null) {
      setHourText(formatPart(value.hour));
      setMinuteText(formatPart(value.minute));
      onValidityChange?.(true);
      return;
    }

    const normalized = formatPart(parsed);
    const nextHourText = part === "hour" ? normalized : hourText;
    const nextMinuteText = part === "minute" ? normalized : minuteText;
    if (part === "hour") {
      setHourText(normalized);
    } else {
      setMinuteText(normalized);
    }
    updateFromText(nextHourText, nextMinuteText);
  };

  const handleInputKeyDown = (
    event: KeyboardEvent<HTMLInputElement>,
    part: TimePart,
  ) => {
    if (event.key !== "ArrowUp" && event.key !== "ArrowDown") {
      return;
    }

    event.preventDefault();
    const currentText = part === "hour" ? hourText : minuteText;
    const currentValue = part === "hour" ? value.hour : value.minute;
    const maximum = part === "hour" ? 23 : 59;
    const direction = event.key === "ArrowUp" ? 1 : -1;
    const current = parsePart(currentText, part) ?? currentValue;
    const next = (current + direction + maximum + 1) % (maximum + 1);
    const nextText = formatPart(next);
    const nextHourText = part === "hour" ? nextText : hourText;
    const nextMinuteText = part === "minute" ? nextText : minuteText;

    if (part === "hour") {
      setHourText(nextText);
    } else {
      setMinuteText(nextText);
    }
    updateFromText(nextHourText, nextMinuteText);
  };

  const handleOptionKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    part: TimePart,
    optionIndex: number,
  ) => {
    const options = part === "hour" ? HOURS : MINUTES;
    let nextIndex: number | null = null;

    if (event.key === "ArrowDown" || event.key === "ArrowRight") {
      nextIndex = (optionIndex + 1) % options.length;
    } else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
      nextIndex = (optionIndex - 1 + options.length) % options.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = options.length - 1;
    }

    if (nextIndex === null) {
      return;
    }

    event.preventDefault();
    selectPart(part, options[nextIndex]);
    const refs = part === "hour" ? hourOptionRefs : minuteOptionRefs;
    refs.current[nextIndex]?.focus();
  };

  const renderOptions = (part: TimePart, options: number[]) => {
    const selectedValue = part === "hour" ? value.hour : value.minute;
    const refs = part === "hour" ? hourOptionRefs : minuteOptionRefs;

    return options.map((option, index) => {
      const isSelected = option === selectedValue;
      return (
        <button
          key={option}
          ref={(element) => {
            refs.current[index] = element;
          }}
          type="button"
          role="option"
          aria-selected={isSelected}
          tabIndex={isSelected || (!options.includes(selectedValue) && index === 0) ? 0 : -1}
          className={`${styles.timeOption} ${
            isSelected ? styles.timeOptionSelected : ""
          }`}
          disabled={disabled}
          onClick={() => selectPart(part, option)}
          onKeyDown={(event) => handleOptionKeyDown(event, part, index)}
        >
          {formatPart(option)}
        </button>
      );
    });
  };

  return (
    <section
      ref={validationTargetRef}
      className={`${styles.timePicker} ${
        presentation === "standalone" ? styles.timePickerStandalone : ""
      } ${className ?? ""}`.trim()}
      aria-label={t("clock_time")}
    >
      <div className={styles.selectedTime} aria-label={t("clock_time")}>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={2}
          value={hourText}
          onChange={(event) => handleInputChange(event, "hour")}
          onBlur={() => handleInputBlur("hour")}
          onKeyDown={(event) => handleInputKeyDown(event, "hour")}
          disabled={disabled}
          aria-label={t("hour")}
          aria-invalid={
            hourText.length !== 2 || parsePart(hourText, "hour") === null
          }
        />
        <span aria-hidden="true">:</span>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={2}
          value={minuteText}
          onChange={(event) => handleInputChange(event, "minute")}
          onBlur={() => handleInputBlur("minute")}
          onKeyDown={(event) => handleInputKeyDown(event, "minute")}
          disabled={disabled}
          aria-label={t("minute")}
          aria-invalid={
            minuteText.length !== 2 || parsePart(minuteText, "minute") === null
          }
        />
      </div>
      <div className={styles.timeColumns}>
        <div className={styles.timeColumnWrapper}>
          <span className={styles.columnLabel}>{t("hour")}</span>
          <div
            className={styles.timeColumn}
            role="listbox"
            aria-label={t("hour")}
          >
            {renderOptions("hour", HOURS)}
          </div>
        </div>
        <div className={styles.timeColumnWrapper}>
          <span className={styles.columnLabel}>{t("minute")}</span>
          <div
            className={styles.timeColumn}
            role="listbox"
            aria-label={t("minute")}
          >
            {renderOptions("minute", MINUTES)}
          </div>
        </div>
      </div>
      {validationMessage}
    </section>
  );
}
